import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import axios from "axios";

// Layouts
import DashboardLayout from "@/layouts/dashboard/DashboardLayout.vue";

// Views
import HomeView from "@/views/HomeView.vue";
import PublishWareView from "@/views/PublishWare.vue";
import Login from "@/views/Login.vue";
import RegisterForm from "@/views/RegisterForm.vue";
import LandingPage from "@/views/LandingPage.vue"; // Public landing page
import PublishTruckView from "@/views/PublishTruck.vue";
import TruckOrWareEntries from "@/views/TruckOrWareEntries.vue";
import RegistrationPending from "@/views/RegistrationPending.vue";
import ResetPassword from "@/views/ResetPassword.vue";
import EmployeesView from "@/views/EmployeesView.vue";
import AddEmployee from "@/views/AddEmployee.vue";
import CompanyDetails from "@/views/CompanyDetails.vue";
import CompanySearch from "@/views/CompanySearch.vue";
import AddPaymentIncident from "@/views/AddPaymentIncident.vue";
import AddTransportIncident from "@/views/AddTransportIncident.vue";
import PolicyPagesDynamic from "@/views/PolicyPagesDynamic.vue";
import IncidentsView from "@/views/IncidentsView.vue";
import RequestResetPassword from "@/views/RequestResetPassword.vue";
import Prices from "@/views/Prices.vue";
import UsefulLinks from "@/views/UsefulLinks.vue";

const routes = [
    {
        path: "/",
        name: "LandingPage",
        component: LandingPage,
        meta: { requiresGuest: true }, // Public page
    },
    {
        path: "/prices",
        name: "Prices",
        component: Prices,
    },
    {
        path: "/useful-links",
        name: "UsefulLinks",
        component: UsefulLinks,
    },
    {
        path: "/policy/:type",
        name: "PolicyPagesDynamic",
        component: PolicyPagesDynamic,
    },
    {
        path: "/login",
        name: "Login",
        component: Login,
        meta: { requiresGuest: true }, // Prevent logged-in users from accessing login
    },
    {
        path: "/request-reset-password",
        name: "RequestResetPassword",
        component: RequestResetPassword,
        meta: { requiresGuest: true },
    },
    {
        path: "/register",
        name: "Register",
        component: RegisterForm,
        meta: { requiresGuest: true }, // Prevent logged-in users from accessing register
    },
    {
        path: "/reset-password/:token?",
        name: "ResetPassword",
        component: ResetPassword,
        meta: { requiresGuest: true },
    },
    {
        path: "/registration-pending",
        name: "RegistrationPending",
        component: RegistrationPending,
        meta: { requiresGuest: true }, // Prevent logged-in users from accessing registration pending
    },
    {
        path: "/dashboard",
        component: DashboardLayout,
        meta: { requiresAuth: true }, // Protect dashboard
        children: [
            {
                path: "",
                name: "Home",
                component: HomeView,
                meta: { breadcrumb: "Dashboard" },
            }, // Default dashboard page
            {
                path: "publish-truck",
                name: "PublishTruckView",
                component: PublishTruckView,
                meta: {
                    title: "sidebar.publishTruck",
                    parent: "sidebar.trucks",
                },
            },
            {
                path: "publish-ware",
                name: "WareView",
                component: PublishWareView,
                meta: { title: "sidebar.publishWare", parent: "sidebar.wares" },
            },
            {
                path: "search-trucks/:uid([0-9a-fA-F-]{36})?",
                name: "SearchTrucks",
                component: TruckOrWareEntries,
                meta: {
                    title: "sidebar.searchTrucks",
                    parent: "sidebar.trucks",
                },
            },
            {
                path: "search-wares/:uid([0-9a-fA-F-]{36})?",
                name: "SearchWares",
                component: TruckOrWareEntries,
                meta: { title: "sidebar.searchWares", parent: "sidebar.wares" },
            },
            {
                path: "my-trucks",
                name: "MyTrucks",
                component: TruckOrWareEntries,
                meta: {
                    title: "sidebar.myTrucks",
                    parent: "sidebar.trucks",
                },
                props: { userEntries: true },
            },
            {
                path: "my-wares",
                name: "MyWares",
                component: TruckOrWareEntries,
                meta: { title: "sidebar.myWares", parent: "sidebar.wares" },
                props: { userEntries: true },
            },
            {
                path: "edit-truck/:uid",
                name: "EditTruck",
                component: () => import("@/views/EditEntry.vue"),
                meta: { title: "Edit Truck", parent: "sidebar.trucks" },
            },
            {
                path: "edit-ware/:uid",
                name: "EditWare",
                component: () => import("@/views/EditEntry.vue"),
                meta: { title: "Edit Ware", parent: "sidebar.wares" },
            },
            {
                path: "employees/:companyId?",
                name: "EmployeesView",
                component: EmployeesView,
                meta: {
                    title: "sidebar.employees",
                    parent: "sidebar.settings",
                },
            },
            {
                path: "add-employee/:id?",
                name: "AddEmployee",
                component: AddEmployee,
                meta: {
                    title: "sidebar.employees",
                    parent: "sidebar.settings",
                },
            },
            {
                path: "company-details/:id?",
                name: "CompanyDetails",
                component: CompanyDetails,
                meta: {
                    title: "sidebar.companyDetails",
                    parent: "sidebar.settings",
                },
            },
            {
                path: "company-search",
                name: "CompanySearch",
                component: CompanySearch,
                meta: {
                    title: "sidebar.companyCheck",
                    parent: "sidebar.settings",
                },
            },
            {
                path: "add-payment-incident",
                name: "AddPaymentIncident",
                component: AddPaymentIncident,
                meta: {
                    title: "addPaymentIncident.title",
                    parent: "sidebar.settings",
                },
            },
            {
                path: "add-transport-incident",
                name: "AddTransportIncident",
                component: AddTransportIncident,
                meta: {
                    title: "addTransportIncident.title",
                    parent: "sidebar.settings",
                },
            },
            {
                path: "my-incidents",
                name: "MyIncidents",
                component: IncidentsView,
                props: {
                    // My Incidents mode:
                    allowCompanySelection: false,
                    initialFilter: "all", // show both “byMe” + “againstMe” by default
                    initialCompanyId: null, // will be defaulted to user.company in mounted()
                },
            },
            {
                path: "other-company-incidents",
                name: "OtherCompanyIncidents",
                component: IncidentsView,
                props: {
                    // Other Companies mode:
                    allowCompanySelection: true,
                    initialFilter: "all", // show all types (payment+transport) by default
                    initialCompanyId: null, // blank = “all companies”
                },
            },
            {
                path: "useful-links",
                name: "DashboardUsefulLinks",
                component: UsefulLinks,
                meta: { hideBanners: true },
            },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach(async (to, _from, next) => {
    const token = localStorage.getItem("accessToken");

    if (to.meta.requiresAuth && !token) {
        return next({ path: "/login", query: { redirect: to.fullPath } });
    }

    next();
});

export default router;
